import {
  type ComponentProps,
  type FC, useCallback,
} from 'react';
import { ImportMethod } from '~/_shared/types/importMethod/importMethod';
import { ModalType } from '~/modal/modalType.enum';
import { useModal } from '~/modal/useModal.hook';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { usePrimarySpreadsheetId } from '~/store/selectors/usePrimarySpreadsheetId';
import { useUserIdSelector } from '~/store/userData/userData.selectors';
import { ImportDataComponent } from './importData.component';
import { useImportFileData } from './useImportFileData';
import { useImportTextData } from './useImportTextData';

export type ImportDataContainerProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  initialImportMethod?: ImportMethod;
}>;

export const ImportDataContainer: FC<ImportDataContainerProps> = ({
  isOpen,
  onClose,
  initialImportMethod,
}: ImportDataContainerProps) => {
  const clientId = useClientIdSelector();
  const userId = useUserIdSelector();
  const spreadsheetId = usePrimarySpreadsheetId();
  const mapId = useMapIdSelector();

  const { replaceCurrentModal: replaceCurrentModalWithImportDataModal } = useModal(ModalType.ImportData);
  const { importFileData, isLoading: isFileImportLoading, errorMessage: errorFileImport, clearErrorMessage: clearFileImportError } = useImportFileData();
  const { importTextData, isLoading: isTextImportLoading, errorMessage: errorTextImport, clearErrorMessage: clearTextImportError } = useImportTextData();

  const onSubmitFileImport: ComponentProps<typeof ImportDataComponent>['onSubmitFileImport'] = useCallback((
    importMethod,
    file,
    onProgressChange,
    onStatusChange,
  ) => {
    if (!clientId || !userId || !mapId || !spreadsheetId) {
      return;
    }

    importFileData({
      clientId, userId, mapId, spreadsheetId, importMethod, file, onProgressChange, onStatusChange, onSuccess: onClose,
    });
  }, [clientId, importFileData, mapId, onClose, spreadsheetId, userId]);

  const onSubmitTextInputImport: ComponentProps<typeof ImportDataComponent>['onSubmitTextInputImport'] = useCallback((
    importMethod,
    input,
  ) => {
    if (!clientId || !userId || !mapId || !spreadsheetId) {
      return;
    }

    importTextData({ clientId, userId, mapId, spreadsheetId, importMethod, input, onSuccess: onClose });
  }, [clientId, importTextData, mapId, onClose, spreadsheetId, userId]);

  const onReplaceImportMethodSelect = useCallback(() => {
    replaceCurrentModalWithImportDataModal({
      initialImportMethod: ImportMethod.Replace,
    });
  }, [replaceCurrentModalWithImportDataModal]);

  return (
    <ImportDataComponent
      isOpen={isOpen}
      onClose={onClose}
      onSubmitFileImport={onSubmitFileImport}
      onSubmitTextInputImport={onSubmitTextInputImport}
      initialImportMethod={initialImportMethod}
      isLoading={isFileImportLoading || isTextImportLoading}
      errorFileImport={errorFileImport}
      errorTextImport={errorTextImport}
      clearFileImportError={clearFileImportError}
      clearTextImportError={clearTextImportError}
      onReplaceImportMethodSelect={onReplaceImportMethodSelect}
    />
  );
};
